import { CLIENT_CONTEXT_FIELDS, type ClientContextColumn } from '../client-context/fields.js';

// ─── Brand Brain presentation registry ───────────────────────────────
// The visible client_context fields are editable through the shared
// onboarding update; Manus-enriched brands_metadata is read-only. This file
// owns only what the approved Product design renders and where; storage and
// validation of client_context live in `client-context/fields.ts`.
//
// The two sources are separate namespaces. A column name present in both
// (`target_audience`, `best_sellers`, `tone_of_voice`, `promotions`,
// `content_strategy`) is two distinct fields with two distinct values, and
// near-namesakes (`product_variance`/`product_variants`, `bundeling`/
// `bundling`, `engagement`/`customer_engagement`, `logistic_support`/
// `logistics_support`) are never merged.

export const ENRICHED_BRAND_TABLE = 'vertex-poc-447312.core.brands_metadata';

/** Design order; a section's number (`01`…`10`) is its position here. */
export const BRAND_BRAIN_SECTIONS = [
    'identity',
    'brand-profile',
    'audience-growth',
    'products',
    'messaging-guidelines',
    'commercial-context',
    'competitive-context',
    'enriched-brand-knowledge',
    'measurement-context',
    'production-kit',
] as const;

export type BrandBrainSectionId = (typeof BRAND_BRAIN_SECTIONS)[number];

export type BrandBrainValueType = 'string' | 'json' | 'numeric';

// ─── client_context fields placed by the Product design ──────────────
// `focus_area`, `non_focus` and `additional_context_from_client` are
// onboarding-only: the design does not place them, so Brand Brain omits them.

export const BRAND_BRAIN_CLIENT_CONTEXT_SECTIONS = {
    customer_name: 'identity',
    industry: 'identity',
    website_url: 'identity',

    brand: 'brand-profile',
    premise: 'brand-profile',
    differentiation: 'brand-profile',
    challenges: 'brand-profile',

    target_audience: 'audience-growth',
    growth_target: 'audience-growth',

    product_to_focus_on: 'products',
    product_variance: 'products',
    best_sellers: 'products',
    bundeling: 'products',
    product_description: 'products',
    product_benefits: 'products',

    tone_of_voice: 'messaging-guidelines',
    messaging_do: 'messaging-guidelines',
    messaging_dont: 'messaging-guidelines',
    things_to_avoid: 'messaging-guidelines',
    content_strategy: 'messaging-guidelines',

    product_pricing_and_discounts: 'commercial-context',
    promotions: 'commercial-context',
    engagement: 'commercial-context',
    logistic_support: 'commercial-context',

    known_competitors: 'competitive-context',
    nemesis: 'competitive-context',
    who_to_ignore: 'competitive-context',
    inspiration_industry: 'competitive-context',

    stickrate_threshold: 'measurement-context',
} as const satisfies Partial<Record<ClientContextColumn, BrandBrainSectionId>>;

export type BrandBrainClientContextColumn = keyof typeof BRAND_BRAIN_CLIENT_CONTEXT_SECTIONS;

export const BRAND_BRAIN_CLIENT_CONTEXT_COLUMNS = Object.keys(
    BRAND_BRAIN_CLIENT_CONTEXT_SECTIONS,
) as BrandBrainClientContextColumn[];

/**
 * Fetched either way. `false` means the design label is still unconfirmed,
 * so the page does not render the column and does not substitute another one.
 *
 * - `customer_name`: design "Brand name" has no confirmed column
 * - `industry`: not placed on the Brand Brain design
 * - `brand`: design "Description" has no confirmed column
 * - `stickrate_threshold`: design "Stickiness / KPI thresholds" is a composite, not this column
 * - `content_strategy`, `engagement`: removed by Product feedback; still stored and still
 *   editable through the Identity Hub onboarding edit route
 */
export const BRAND_BRAIN_CLIENT_CONTEXT_VISIBLE = {
    customer_name: false,
    industry: false,
    website_url: true,
    brand: false,
    premise: true,
    differentiation: true,
    challenges: true,
    target_audience: true,
    growth_target: true,
    product_to_focus_on: true,
    product_variance: true,
    best_sellers: true,
    bundeling: true,
    product_description: true,
    product_benefits: true,
    tone_of_voice: true,
    messaging_do: true,
    messaging_dont: true,
    things_to_avoid: true,
    content_strategy: false,
    product_pricing_and_discounts: true,
    promotions: true,
    engagement: false,
    logistic_support: true,
    known_competitors: true,
    nemesis: true,
    who_to_ignore: true,
    inspiration_industry: true,
    stickrate_threshold: false,
} as const satisfies Record<BrandBrainClientContextColumn, boolean>;

/**
 * What Brand Brain may change: every rendered client_context field the
 * onboarding registry lets an edit touch. Hidden fields stay unplaced, so
 * they are not editable here; `customer_name` and `industry` are locked.
 */
export const BRAND_BRAIN_EDITABLE_COLUMNS = BRAND_BRAIN_CLIENT_CONTEXT_COLUMNS.filter(
    (column) => BRAND_BRAIN_CLIENT_CONTEXT_VISIBLE[column] && CLIENT_CONTEXT_FIELDS[column].write === 'editable',
);

export function isBrandBrainEditableColumn(value: string): boolean {
    return (BRAND_BRAIN_EDITABLE_COLUMNS as string[]).includes(value);
}

// ─── Nexus-owned fields ──────────────────────────────────────────────
// Brand context Nexus keeps itself: stored only in Postgres, beside the
// client_context copy, and never read from or written to BigQuery. They are
// deliberately not client_context columns, so the BigQuery readers, writers
// and onboarding syncs never see them. Storage and validation live in
// `nexus-fields.ts`; Brand Brain is their only editor.

export const BRAND_BRAIN_NEXUS_SECTIONS = {
    approved_claims: 'messaging-guidelines',
    what_works: 'messaging-guidelines',
    visual_production_rules: 'production-kit',
} as const satisfies Record<string, BrandBrainSectionId>;

export type BrandBrainNexusColumn = keyof typeof BRAND_BRAIN_NEXUS_SECTIONS;

export const BRAND_BRAIN_NEXUS_COLUMNS = Object.keys(BRAND_BRAIN_NEXUS_SECTIONS) as BrandBrainNexusColumn[];

export function isBrandBrainNexusColumn(value: string): value is BrandBrainNexusColumn {
    return Object.prototype.hasOwnProperty.call(BRAND_BRAIN_NEXUS_SECTIONS, value);
}

/**
 * Row order within a section, across both sources: the client_context fields in
 * registry order, with the approved claims and what works right after the
 * messaging don'ts.
 */
export const BRAND_BRAIN_FIELD_ORDER: readonly string[] = BRAND_BRAIN_CLIENT_CONTEXT_COLUMNS.flatMap((column) =>
    column === 'messaging_dont' ? [column, 'approved_claims', 'what_works'] : [column],
).concat('visual_production_rules');

// ─── core.brands_metadata (Manus-enriched, read-only) ────────────────
// Every column is STRING in BigQuery. `visible: false` fields are fetched
// and returned but not rendered until Product places them. The five `shown`
// columns are the design rows that map cleanly; `revenue_model` was removed
// from the page by Product feedback. The rest stay hidden,
// including columns that used to be visible: the design's two unread
// enriched labels are unconfirmed, so nothing is substituted for them.

export interface EnrichedBrandFieldDefinition {
    visible: boolean;
    section: 'enriched-brand-knowledge';
}

const shown: EnrichedBrandFieldDefinition = { visible: true, section: 'enriched-brand-knowledge' };
const hidden: EnrichedBrandFieldDefinition = { visible: false, section: 'enriched-brand-knowledge' };

export const ENRICHED_BRAND_FIELDS = {
    industry_category: shown,
    primary_geography: shown,
    product_categories: shown,
    revenue_model: hidden,
    hero_product: shown,
    growth_trend: shown,

    sku_count: hidden,
    price_tiers: hidden,
    tech_stack: hidden,
    revenue_estimate: hidden,
    channel_sow: hidden,
    paid_vs_organic_traffic: hidden,
    urgency_usage_frequency: hidden,
    target_audience: hidden,
    sub_market: hidden,
    physical_products: hidden,

    product_variants: hidden,
    best_sellers: hidden,
    bundling: hidden,
    tone_of_voice: hidden,
    promotions: hidden,
    content_strategy: hidden,
    customer_engagement: hidden,
    logistics_support: hidden,
    competitor_differentiation: hidden,
    one_line_brand_summary: hidden,
    business_model_summary: hidden,
    acquisition_mix_summary: hidden,
    assortment_summary: hidden,
} as const satisfies Record<string, EnrichedBrandFieldDefinition>;

export type EnrichedBrandColumn = keyof typeof ENRICHED_BRAND_FIELDS;

export const ENRICHED_BRAND_COLUMNS = Object.keys(ENRICHED_BRAND_FIELDS) as EnrichedBrandColumn[];

export function isEnrichedBrandColumn(value: string): value is EnrichedBrandColumn {
    return Object.prototype.hasOwnProperty.call(ENRICHED_BRAND_FIELDS, value);
}

export type BrandBrainFieldSource = 'clientContext' | 'enriched' | 'nexus';

/** Source-qualified field key, because a column name can exist in both sources. */
export function brandBrainFieldKey(source: BrandBrainFieldSource, column: string): string {
    return `${source}:${column}`;
}

/** A field with no usable value: null, blank text, or an empty JSON array/object. */
export function isBrandBrainValueGap(value: unknown): boolean {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
}
